import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const BACKEND_URL = "https://zenvy-backend-e0zx.onrender.com";
const API = `${BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState(() => {
    const stored = localStorage.getItem('zenvy_tokens');
    return stored ? JSON.parse(stored) : null;
  });

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    if (!tokens?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // Token invalid, clear storage
        localStorage.removeItem('zenvy_tokens');
        setTokens(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  }, [tokens]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
  const response = await fetch(`${API}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.detail || data.message || 'Login failed');
  }

  localStorage.setItem('zenvy_tokens', JSON.stringify({
    access_token: data.access_token,
    refresh_token: data.refresh_token
  }));

  setTokens({
    access_token: data.access_token,
    refresh_token: data.refresh_token
  });

  setUser(data.user);
  return data.user;
};

  const register = async (userData) => {
    const response = await fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const data = await response.json();
    localStorage.setItem('zenvy_tokens', JSON.stringify({
      access_token: data.access_token,
      refresh_token: data.refresh_token
    }));
    setTokens({ access_token: data.access_token, refresh_token: data.refresh_token });
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    const response = await fetch(`${API}/auth/google/session?session_id=${sessionId}`, {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error('Google authentication failed');
    }

    const data = await response.json();
    localStorage.setItem('zenvy_tokens', JSON.stringify({
      access_token: data.access_token,
      refresh_token: data.refresh_token
    }));
    setTokens({ access_token: data.access_token, refresh_token: data.refresh_token });
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('zenvy_tokens');
    setTokens(null);
    setUser(null);
  };

  const getAuthHeader = () => {
    return tokens?.access_token ? { 'Authorization': `Bearer ${tokens.access_token}` } : {};
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      loginWithGoogle,
      handleGoogleCallback,
      logout,
      getAuthHeader,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
